import React, { createContext, useContext, useEffect, useState } from "react";

const dict = {
  en: {
    nav: {
      whyInfraAI: "Why InfraAI",
      gap: "Monitoring Gap",
      about: "About",
      solutions: "Solutions",
      services: "Corporate Services",
      techSecurity: "Technology & Security",
      company: "Company",
      partners: "Supported Stack",
      certificates: "Certificates",
      references: "References",
      contact: "Contact",
      support: "Support",
      demo: "Contact Us",
      login: "Admin",
      solutionsMenu: [
        { k: "AI Capabilities",     v: "End-to-end infrastructure intelligence",      href: "#capabilities" },
        { k: "AI Engine Hub",         v: "Live orchestration · unified data model",     href: "#platform" },
        { k: "RCA Flow",              v: "Classify → diagnose → AI report",             href: "#rca" },
        { k: "Why InfraAI?",          v: "From reactive monitoring to autonomous ops",  href: "#why-infraai" },
      ],
      servicesMenu: [
        { k: "Architecture & Planning",  v: "Infrastructure analysis & integration design", href: "#services" },
        { k: "Deployment & Integration", v: "Secure rollout across your stack",           href: "#services" },
        { k: "Managed Services",         v: "Full operational ownership",                 href: "#services" },
        { k: "Training",                 v: "User, admin & technical enablement",         href: "#services" },
      ],
      techMenu: [
        { k: "Technology Integrations",  v: "Oracle · MSSQL · PostgreSQL · Cloud",        href: "#partners" },
        { k: "AI Platform Architecture", v: "On-prem AI engine · hybrid & local LLM",     href: "#platform" },
        { k: "Security & Compliance",    v: "100% on-prem · audit-ready · regulated",     href: "#trust" },
        { k: "Supported Platforms",      v: "Enterprise stack · DB · cloud · middleware", href: "#partners" },
      ],
    },
    about: {
      eyebrow: "About",
      title: "20+ Years of Field Experience, Local Engineering Power",
      lead: "Developed with Veridata IT assurance, InfraAI automates the operational burden of enterprise infrastructure.",
      stats: [
        { v: "20+", k: "Years in enterprise IT" },
        { v: "150+", k: "Production engagements" },
        { v: "12", k: "Sovereign regions" },
        { v: "AI/ML", k: "First-party engineering" },
      ],
    },
    services: {
      eyebrow: "Services",
      title: "Corporate Services",
      sub: "All the expertise you need for successful InfraAI deployment and peak performance — delivered under one roof.",
      items: [
        {
          k: "Architecture & Planning",
          v: "We analyze your infrastructure, design the optimal InfraAI architecture and build the integration plan together with you.",
          icon: "Compass",
        },
        {
          k: "Deployment & Integration",
          v: "We deploy InfraAI in your environment and integrate it securely with server, database, middleware, network and your existing monitoring tools.",
          icon: "Wrench",
        },
        {
          k: "24/7 Technical Support",
          v: "Our expert engineers provide 24/7 support, proactive maintenance and advanced technical advisory when you need it.",
          icon: "Headphones",
        },
        {
          k: "Training & Enablement",
          v: "We deliver comprehensive training for user, administrator and technical teams to get the most from InfraAI.",
          icon: "GraduationCap",
        },
        {
          k: "Managed InfraAI",
          v: "If you prefer, we handle model management, optimization and operational monitoring on your behalf.",
          icon: "Sparkles",
        },
        {
          k: "Environment-Specific AI Models",
          v: "We analyze your infrastructure, develop AI models tailored to your organization and continuously improve accuracy.",
          icon: "Brain",
        },
      ],
    },
    partners: {
      eyebrow: "Integration & Coverage",
      title: "Built for Enterprise Infrastructure",
      sub: "InfraAI integrates seamlessly with your existing database, operating system, middleware and monitoring stack thanks to its vendor-agnostic architecture. It protects your current investments and does not create new platform or vendor dependency.",
      gridLabel: "SUPPORTED TECHNOLOGIES",
      gridCount: "12+ Ecosystem Integrations",
      footerQuote: "No vendor lock-in. InfraAI stays neutral and integrates seamlessly across your entire enterprise stack.",
      groups: [
        { k: "DATABASES",                  v: ["Oracle", "Microsoft SQL Server", "PostgreSQL", "TimescaleDB"] },
        { k: "INFRASTRUCTURE & CLOUD",     v: ["Red Hat", "Microsoft", "AWS", "Azure", "Google Cloud"] },
        { k: "MIDDLEWARE & APP SERVERS",   v: ["Oracle WebLogic", "Apache Tomcat", "Nginx", "IIS"] },
        { k: "LOCAL AI COMPONENTS",        v: ["Ollama", "Llama 3.1", "Phi-4", "PyTorch"] },
      ],
    },
    certificates: {
      eyebrow: "Certifications",
      title: "Audit-ready by design",
      sub: "Our platform and our operating company are aligned with the standards regulated buyers actually evaluate.",
      items: [
        { k: "ISO/IEC 27001",  v: "Information security management" },
        { k: "ISO/IEC 27017",  v: "Cloud security controls" },
        { k: "ISO/IEC 27018",  v: "PII in public clouds" },
        { k: "SOC 2 Type II",  v: "Trust services criteria · annual" },
        { k: "GDPR / KVKK",    v: "Data protection · EU + Türkiye" },
        { k: "TS EN 9001",     v: "Quality management" },
      ],
    },
    references: {
      eyebrow: "References",
      title: "Trusted by security teams across regulated industries",
      sub: "Customer names disclosed under reference programs only. Sector profile shown below.",
      ndaNote: "Customer names disclosed under NDA via the reference program.",
      sectors: [
        { k: "Financial Services", v: "Tier-1 banks · global insurers" },
        { k: "SaaS & Technology",  v: "High-growth platforms · ISVs" },
        { k: "Healthcare",         v: "Hospital networks · medical devices" },
        { k: "Energy & Utilities", v: "Critical infrastructure operators" },
        { k: "Public Sector",      v: "Government · defense suppliers" },
        { k: "Manufacturing",      v: "Industrial control · OT" },
      ],
    },
    support: {
      eyebrow: "Support",
      title: "Enterprise SLA — humans, not just docs",
      sub: "Every InfraAI tenant gets a named operator team and a 24/7 escalation path. The engineers who wrote the engine answer your tier-3.",
      channels: [
        { k: "Email",    v: "support@veridatait.com" },
        { k: "Hotline",  v: "+90 (212) 000-0000" },
        { k: "Portal",   v: "https://veridatait.com/support" },
        { k: "Sev-1",    v: "<15min response · 24/7" },
      ],
    },
    contact: {
      eyebrow: "Get Started",
      title: "Discover InfraAI on Your Own Infrastructure",
      sub: "In a live demo with our expert team, explore how InfraAI analyzes your infrastructure, identifies root cause and produces actionable recommendations — together.",
      trustBand: [
        {
          k: "Live Product Demo",
          v: "See InfraAI's real screens, AI engine and end-to-end analysis workflows.",
        },
        {
          k: "Infrastructure-Specific Assessment",
          v: "Evaluate use cases tailored to your server, database, middleware and network stack.",
        },
        {
          k: "Deployment & Integration Plan",
          v: "Build a practical rollout plan aligned with your monitoring tools, operations and security requirements.",
        },
      ],
      email: "info@infraai.ai",
      emailLabel: "Enterprise enquiries",
      ctaEmail: "Email us to schedule a demo",
    },
    hero: {
      eyebrow: "Continuous Infrastructure Assurance",
      slideParts: [
        [
          { text: "Is your infrastructure " },
          { text: "secure?", cyan: true },
        ],
        [
          { text: "In today's world, monitoring alone is " },
          { text: "no longer enough.", cyan: true },
        ],
        [
          { text: "Traditional monitoring tools " },
          { text: "don't prevent outages.", cyan: true },
        ],
        [
          { text: "An intelligent infrastructure management system that " },
          { text: "learns", cyan: true },
          { text: ", " },
          { text: "predicts", cyan: true },
          { text: ", and " },
          { text: "solves", cyan: true },
          { text: " with AI." },
        ],
      ],
      slides: [
        "Is your infrastructure secure?",
        "In today's world, monitoring alone is no longer enough.",
        "Traditional monitoring tools don't prevent outages.",
        "An intelligent infrastructure management system that learns, predicts, and solves with AI.",
      ],
      brandName: "InfraAI",
      brandTagline: "Next-Gen Agentic AIOps-Powered Monitoring Center",
      ctaPrimary: "Request Live Demo",
      ctaSecondary: "See the AI Engine",
      badges: ["24 AI Models", "100% On-Prem AI", "AI-Driven RCA", "4-Layer Coverage"],
      live: "LIVE AI ENGINE",
      decision: "decision · validated",
      decisionAgo: "2.4s ago",
    },
    whyInfraAI: {
      eyebrow: "Why InfraAI",
      title: "Why ",
      titleSuffix: "?",
      thesisParts: [
        { text: "Most monitoring tools tell you ", bold: false },
        { text: "what happened", bold: true },
        { text: ". InfraAI tells you ", bold: false },
        { text: "why", cyan: true },
        { text: " it happened, ", bold: false },
        { text: "what comes next", cyan: true },
        { text: ", and ", bold: false },
        { text: "what to do", cyan: true },
        { text: ". Monitoring warns you — InfraAI prepares you.", bold: false },
      ],
      chain: [
        { n: "01", verb: "See", desc: "Collects metrics, fires alerts when thresholds break.", rival: true, rivalLabel: "Current monitoring stops here" },
        { n: "02", verb: "Understand", desc: "Correlates events across layers and finds root cause.", rival: false },
        { n: "03", verb: "Predict", desc: "Forecasts outcomes days before the problem hits.", rival: false },
        { n: "04", verb: "Solve", desc: "Delivers actionable steps and ready-to-run commands.", rival: false },
      ],
      pillars: [
        {
          k: "It thinks",
          v: "Looks at relationships between events — not isolated metrics. Surfaces the real reason behind the alert.",
          icon: "brain",
          evidence: [
            [
              { t: "process:", tone: "dim" },
              { t: "java", tone: "violet", tag: true },
              { t: " · RAM ", tone: "base" },
              { t: "42MB → 312MB", tone: "dim" },
              { t: " (7d)", tone: "dim" },
            ],
            [
              { t: "→ ", tone: "arrow" },
              { t: "+643% growth · ", tone: "base" },
              { t: "memory leak suspected", tone: "amber" },
            ],
          ],
        },
        {
          k: "It predicts",
          v: "Warns days before disk fills, RAM runs out, or services degrade. Proactive, not reactive.",
          icon: "trend",
          evidence: [
            [
              { t: "/u01 disk ", tone: "dim" },
              { t: "94%", tone: "cyan", tag: true },
              { t: " · ", tone: "base" },
              { t: "+0.8/day", tone: "dim" },
            ],
            [
              { t: "→ ", tone: "arrow" },
              { t: "fills in ~22 days", tone: "amber" },
            ],
          ],
        },
        {
          k: "It decides",
          v: "No need to scan hundreds of metrics. Separates real anomalies from a 30-day baseline.",
          icon: "target",
          evidence: [
            [
              { t: "SRV-A", tone: "dim" },
              { t: " drift 0.6% ", tone: "base" },
              { t: "→ ", tone: "arrow" },
              { t: "no alert", tone: "green" },
            ],
            [
              { t: "SRV-B", tone: "dim" },
              { t: " drift 38% ", tone: "base" },
              { t: "→ ", tone: "arrow" },
              { t: "warning", tone: "red" },
            ],
          ],
        },
        {
          k: "It acts",
          v: "Doesn't just alert. Gives you the command and the fix step.",
          icon: "zap",
          evidence: [
            [
              { t: "PROD-04", tone: "green", tag: true },
              { t: " CPU 98% · ", tone: "base" },
              { t: "cause: db service", tone: "dim" },
            ],
            [
              { t: "→ ", tone: "arrow" },
              { t: "top -bn1 -o %CPU | head -15", tone: "cyan" },
            ],
          ],
        },
      ],
      stats: [
        { v: "4", k: "layers" },
        { v: "24", k: "AI models" },
        { v: "1", k: "decision engine" },
      ],
      statsTail: "— turning data into action.",
    },
    gap: {
      eyebrow: "The Monitoring Gap",
      title: "Monitoring shows data. InfraAI explains the truth.",
      cards: [
        {
          k: "Monitoring ≠ Understanding",
          v: "Existing monitoring systems see metrics. They don't reason across layers — or tell you the slow query at 11:42 and the JVM pause at 11:43 are the same incident.",
        },
        {
          k: "Why · Next · Action",
          v: "They show that something broke — not why, what happens next, or what you should do. InfraAI answers all three in one evidence chain.",
        },
        {
          k: "Data ≠ Decision",
          v: "They report \"RAM at 88%.\" They won't tell you which process is leaking, how many days until disk is full, or the exact steps to run at 3 AM.",
        },
      ],
    },
    platform: {
      eyebrow: "Four-Layer Intelligence",
      title: "Monitor. Analyze. Predict. Optimize.",
      tagline: "One platform. One data model. End-to-end infrastructure intelligence.",
      sub: "An AI platform that understands your entire infrastructure, performs root cause analysis, and recommends actionable steps.",
      layers: [
        {
          n: "01",
          k: "InfraAI Discover",
          v: "Continuous telemetry across database, server, network and middleware layers. One agent set, one unified signal model — proactive 24/7 anomaly detection from the moment you deploy.",
          bullets: ["DB · Server · Network · Middleware", "Process / GC / heap / thread pool", "Latency · connectivity · DNS · SSL"],
        },
        {
          n: "02",
          k: "InfraAI Reason",
          v: "32-category RCA (root cause analysis) classification engine, diagnostics within safe boundaries and automated report generation on a local LLM. Every finding ships with reproducible evidence and a concrete fix.",
          bullets: ["32 RCA categories", "7-layer security barrier (guardrail)", "Local LLM infrastructure — full data privacy"],
        },
        {
          n: "03",
          k: "InfraAI Predict",
          v: "Prophet and LSTM models project CPU, RAM and disk resource requirements across a 5-minute to 90-day horizon. Seasonal baselines separate normal patterns from real anomalies.",
          bullets: ["5 minutes to 90 days forecast horizon", "Univariate and multivariate analysis (seasonality-aware)", "24 orchestrated AI engines"],
        },
        {
          n: "04",
          k: "InfraAI Optimize",
          v: "SQL analysis, index and rewrite recommendations, cost optimization (rightsizing) and automated remediation playbooks — with measurable gain estimates your IT leadership can approve.",
          bullets: ["SQL optimization", "Cost rightsizing", "One-click safe automation"],
        },
      ],
    },
    rca: {
      eyebrow: "Infra AI RCA Flow Diagram",
      titleLead: "How Does ",
      title: "Interpret Metrics?",
      sub: "Correlates metrics from server, database, middleware and network layers to determine root cause and produce actionable recommendations.",
      steps: [
        {
          n: "01",
          k: "Classifier Engine",
          v: "32 RCA categories. Signal arrives, classifier picks the family (lock contention, memory leak, IO saturation, etc.) with a confidence score.",
          code: `{
  "incident": "INC-8429",
  "category": "DB_LOCK_CONTENTION",
  "confidence": 0.962,
  "engines_triggered": ["ML.classifier-v3", "rule.db.adv-lock"]
}`,
        },
        {
          n: "02",
          k: "Diagnostic Query",
          v: "Scoped, read-only diagnostic queries fire over SSH / WinRM / SQL. Every command is filtered, timed-out and replay-logged.",
          code: `[SSH] linux-prod-07 :: vmstat 1 3 · iostat -xz 1 3
[SQL] db://prod :: session_blockers (read-only)
[WinRM] win-app-02 :: Get-Counter '\\Process(*)\\Working Set'
guardrails: command_filter · timeout · sensitive_redaction`,
        },
        {
          n: "03",
          k: "AI Report Generator",
          v: "A local LLM receives structured evidence and emits a human-readable root cause plus a remediation plan — validated against a hallucination check.",
          code: `Root cause:  Long-running UPDATE holding TX lock
              blocked 14 sessions for 38s.
Trigger:     Missing index on filter column
Fix:         CREATE INDEX idx_orders_customer ON ORDERS(customer_id);
Confidence:  91% · hallucination_check: PASS`,
        },
      ],
    },
    metrics: {
      eyebrow: "Lab & pilot deployment results",
      title: "First 90-day performance in pilot deployments",
      stats: [
        { v: "14×", k: "ROI on infra incidents", sub: "vs. monitoring-only response model" },
        { v: "90.7%", k: "Average accuracy", sub: "RCA + AI analysis, validated" },
        { v: "24", k: "Orchestrated engines", sub: "ML · native · statistical · rules" },
        { v: "74%", k: "Faster MTTR", sub: "vs. existing monitoring baseline" },
      ],
    },
    capabilities: {
      eyebrow: "AI Operations Platform",
      titleWordmark: true,
      title: "Beyond Monitoring — Infrastructure Intelligence",
      sub: "An enterprise management platform that analyzes server, database, middleware and network layers end-to-end — detecting anomalies, root causes and potential system impact with AI.",
      hook: {
        before: "Monitoring tools show the problem.",
        afterLead: "finds the cause, analyzes the impact and recommends what to do next.",
      },
      cardsTitle: "AI Capabilities",
      previewTagline: "Your Strategic Technology Partner",
      verticals: [
        {
          k: "Event Correlation & Linking",
          icon: "brain",
          bullets: [
            "Cross-layer event relationship analysis",
            "Automatic incident chain construction",
            "Distributed alarm correlation",
            "End-to-end visibility in a single pane",
          ],
        },
        {
          k: "Automated RCA",
          icon: "search",
          bullets: [
            "Automated root cause analysis reports",
            "Affected systems and services identification",
            "Prioritized probable causes",
            "Remediation recommendations and action plans",
          ],
        },
        {
          k: "Predictive Intelligence & Capacity",
          icon: "trend",
          bullets: [
            "Performance trend analysis",
            "Resource exhaustion forecasting",
            "Capacity planning support",
            "Risk score generation",
          ],
        },
        {
          k: "Autonomous Decision Engine",
          icon: "zap",
          bullets: [
            "AI-powered action recommendations",
            "Prioritized incident management",
            "Alert noise reduction",
            "Decision support mechanism",
          ],
        },
        {
          k: "SQL & Database Intelligence",
          icon: "layers",
          bullets: [
            "Critical and slow SQL detection",
            "Index and rewrite recommendations",
            "Database performance analysis",
            "Capacity and bottleneck forecasting",
          ],
        },
        {
          k: "Hybrid & Local AI (On-Prem LLM)",
          icon: "lock",
          bullets: [
            "On-prem or hybrid architecture",
            "AI analysis without data egress",
            "Local LLM support",
            "Secure and auditable AI infrastructure",
          ],
        },
      ],
    },
    enterpriseProof: {
      items: [
        { v: "Production Proven", accent: true },
        { v: "13 AI Analysis Engines" },
        { v: "100% On-Premise Architecture" },
      ],
      coverage: "Full-stack coverage across: Database · Operating Systems · Network · Storage · Middleware",
      stack: "Oracle · MSSQL · PostgreSQL · Linux · Windows",
      cta: "Book Technical Demo →",
    },
    trust: {
      eyebrow: "Architecture & Compliance",
      title: "Built for Critical Infrastructure. Regulation-Ready.",
      sub: "Traditional monitoring tells you what happened. InfraAI predicts what will happen and recommends what to do next.",
      items: [
        { k: "100% On-Premises Architecture", v: "Your data, prompts and telemetry metrics never leave your secure network. Zero cloud dependency — full data sovereignty for regulated environments (Finance, Public Sector, Energy)." },
        { k: "Day-One Optimization", v: "No long training or data collection cycles. Pre-configured AI models start risk detection and analysis at installation." },
        { k: "Enterprise Security Controls", v: "Command filtering, prompt injection protection, sensitive data masking and full audit trail infrastructure — meeting internal and external audit standards." },
        { k: "Proactive SQL & Performance Analysis", v: "Detects and reports critical and slowing SQL queries, database performance issues and capacity bottlenecks before outages occur." },
      ],
    },
    stack: {
      eyebrow: "Tech Stack",
      title: "Built on a stack regulated industries already trust",
      groups: [
        { k: "Frontend", v: "React · TypeScript · Tailwind · Recharts" },
        { k: "Backend", v: "FastAPI · SQLAlchemy · Python 3.11" },
        { k: "Data", v: "TimescaleDB · PostgreSQL · Redis" },
        { k: "AI/ML", v: "Prophet · LSTM · IsolationForest · RandomForest · DecisionTree" },
        { k: "LLM", v: "Ollama · Llama 3.1 · Phi-4 (local)" },
        { k: "Connectivity", v: "SSH · WinRM · SQL native · Native DB AI" },
      ],
    },
    footer: {
      tagline: "Intelligent Infrastructure Solutions",
      company: "Powered by Veridata IT",
      rights: "All rights reserved.",
      platformLabel: "Platform",
      companyLabel: "Company",
      legalLabel: "Legal",
      platformLinks: [
        { k: "AI Capabilities", href: "#capabilities" },
        { k: "AI Engine Hub", href: "#platform" },
        { k: "RCA Flow", href: "#rca" },
        { k: "Why InfraAI?", href: "#why-infraai" },
        { k: "Security & Compliance", href: "#trust" },
      ],
    },
  },
  tr: {
    nav: {
      whyInfraAI: "Neden InfraAI",
      gap: "İzleme Boşluğu",
      about: "Hakkımızda",
      solutions: "Çözümler",
      services: "Kurumsal Hizmetler",
      techSecurity: "Teknoloji & Güvenlik",
      company: "Kurumsal",
      partners: "Desteklenen Teknolojiler",
      certificates: "Sertifikalar",
      references: "Referanslar",
      contact: "İletişim",
      support: "Destek",
      demo: "İletişime Geçin",
      login: "Yönetici",
      solutionsMenu: [
        { k: "AI Yetkinlikleri",            v: "İzlemenin ötesinde altyapı zekâsı",          href: "#capabilities" },
        { k: "AI Engine Hub",               v: "Canlı orkestrasyon · tek veri modeli",       href: "#platform" },
        { k: "RCA Akışı",                   v: "Sınıflandır → diagnostik → AI rapor",        href: "#rca" },
        { k: "Neden InfraAI?",              v: "Reaktif izlemeden otonom yönetime",          href: "#why-infraai" },
      ],
      servicesMenu: [
        { k: "Mimari ve Planlama",          v: "Altyapı analizi & entegrasyon tasarımı",     href: "#services" },
        { k: "Kurulum ve Entegrasyon",      v: "Güvenli kurulum & stack entegrasyonu",       href: "#services" },
        { k: "Yönetilen Hizmetler",         v: "Tam operasyon yönetimi",                     href: "#services" },
        { k: "Eğitim",                      v: "Kullanıcı, yönetici & teknik ekip",           href: "#services" },
      ],
      techMenu: [
        { k: "Teknoloji Entegrasyonları",   v: "Oracle · MSSQL · PostgreSQL · Cloud",         href: "#partners" },
        { k: "AI Platform Mimarisi",        v: "On-prem AI motor · hibrit & yerel LLM",      href: "#platform" },
        { k: "Güvenlik ve Uyumluluk",       v: "%100 on-prem · denetim hazır · regüle",        href: "#trust" },
        { k: "Desteklenen Platformlar",     v: "Enterprise stack · DB · cloud · middleware",  href: "#partners" },
      ],
    },
    about: {
      eyebrow: "Hakkımızda",
      title: "20+ Yıllık Saha Deneyimi, Yerli Mühendislik Gücü",
      lead: "Veridata IT güvencesiyle geliştirilen InfraAI, enterprise altyapıların yönetim yükünü otonomlaştırıyor.",
      stats: [
        { v: "20+", k: "Yıl kurumsal BT deneyimi" },
        { v: "150+", k: "Üretim projesi" },
        { v: "4", k: "Katman kapsamı", sub: "Veritabanı · Sunucu · Orta Katman · Network" },
        { v: "AI/ML", k: "Kendi mühendisliğimiz" },
      ],
    },
    services: {
      eyebrow: "Hizmetler",
      title: "Kurumsal Hizmetler",
      sub: "InfraAI'nin başarılı bir şekilde devreye alınması ve maksimum verimle çalışması için ihtiyaç duyduğunuz tüm uzmanlık tek çatı altında sunulur.",
      items: [
        {
          k: "Mimari ve Planlama",
          v: "Altyapınızı analiz ediyor, InfraAI için en uygun mimariyi tasarlıyor ve entegrasyon planını birlikte oluşturuyoruz.",
          icon: "Compass",
        },
        {
          k: "Kurulum ve Entegrasyon",
          v: "InfraAI'yi ortamınıza kuruyor; sunucu, veritabanı, middleware, network ve mevcut izleme araçlarınızla güvenli şekilde entegre ediyoruz.",
          icon: "Wrench",
        },
        {
          k: "7/24 Teknik Destek",
          v: "Uzman mühendislerimizle 7/24 destek sunuyor, proaktif bakım sağlıyor ve ihtiyaç duyduğunuzda ileri seviye teknik danışmanlık veriyoruz.",
          icon: "Headphones",
        },
        {
          k: "Eğitim ve Yetkinlik",
          v: "Kullanıcı, yönetici ve teknik ekiplerin InfraAI'yi en verimli şekilde kullanabilmesi için kapsamlı eğitimler düzenliyoruz.",
          icon: "GraduationCap",
        },
        {
          k: "Yönetilen InfraAI",
          v: "Dilerseniz InfraAI'nin model yönetimini, optimizasyonunu ve operasyonel takibini sizin adınıza gerçekleştiriyoruz.",
          icon: "Sparkles",
        },
        {
          k: "Ortama Özel AI Modelleri",
          v: "Altyapınızı analiz ediyor, kurumunuza özel yapay zekâ modelleri geliştiriyor ve doğruluk oranını sürekli iyileştiriyoruz.",
          icon: "Brain",
        },
      ],
    },
    partners: {
      eyebrow: "Entegrasyon & Kapsam",
      title: "Kurumsal Altyapılar İçin Tasarlandı",
      sub: "InfraAI, üreticiden bağımsız (vendor-agnostic) mimarisi sayesinde mevcut veritabanı, işletim sistemi, middleware ve izleme altyapınıza sorunsuz şekilde entegre olur. Mevcut yatırımlarınızı korur, yeni bir platform veya satıcı bağımlılığı oluşturmaz.",
      gridLabel: "DESTEKLENEN TEKNOLOJİLER",
      gridCount: "12+ Ekosistem Entegrasyonu",
      footerQuote: "Üretici ve Ürün Bağımsızlığı: Belirli bir teknolojiye, ürüne veya markaya bağlı kalmadan altyapınızı özgürce yönetmenizi ve geliştirebilmenizi sağlar.",
      groups: [
        { k: "VERİTABANI",           v: ["Oracle", "Microsoft SQL Server", "PostgreSQL", "TimescaleDB"] },
        { k: "ALTYAPI & BULUT",      v: ["Red Hat", "Microsoft", "AWS", "Azure", "Google Cloud"] },
        { k: "MIDDLEWARE",           v: ["Oracle WebLogic", "Apache Tomcat", "Nginx", "IIS"] },
        { k: "YEREL AI / LLM",       v: ["Ollama", "Llama 3.1", "Phi-4", "PyTorch"] },
      ],
    },
    certificates: {
      eyebrow: "Sertifikalar",
      title: "Tasarımdan denetime hazır",
      sub: "Platformumuz ve işletmeci şirketimiz, düzenlemeli alıcıların gerçekten değerlendirdiği standartlarla uyumludur.",
      items: [
        { k: "ISO/IEC 27001",  v: "Bilgi güvenliği yönetimi" },
        { k: "ISO/IEC 27017",  v: "Bulut güvenliği kontrolleri" },
        { k: "ISO/IEC 27018",  v: "Public cloud'da PII" },
        { k: "SOC 2 Type II",  v: "Trust services criteria · yıllık" },
        { k: "GDPR / KVKK",    v: "Veri koruma · AB + Türkiye" },
        { k: "TS EN 9001",     v: "Kalite yönetim sistemi" },
      ],
    },
    references: {
      eyebrow: "Referanslar",
      title: "Düzenlemeli sektörlerdeki güvenlik ekiplerinin tercihi",
      sub: "Müşteri isimleri yalnızca referans programları kapsamında paylaşılır. Sektör profili aşağıdadır.",
      ndaNote: "Müşteri isimleri NDA kapsamında referans programı ile paylaşılır.",
      sectors: [
        { k: "Finansal Hizmetler", v: "Tier-1 bankalar · global sigorta" },
        { k: "SaaS & Teknoloji",   v: "Hızlı büyüyen platformlar · ISV'ler" },
        { k: "Sağlık",             v: "Hastane ağları · medikal cihazlar" },
        { k: "Enerji & Altyapı",   v: "Kritik altyapı operatörleri" },
        { k: "Kamu",               v: "Devlet · savunma tedarikçileri" },
        { k: "Üretim",             v: "Endüstriyel kontrol · OT" },
      ],
    },
    support: {
      eyebrow: "Destek",
      title: "Kurumsal SLA — sadece doküman değil, gerçek insanlar",
      sub: "Her InfraAI kiracısı için isimlendirilmiş operatör ekibi ve 7/24 eskalasyon hattı. Motoru yazan mühendisler tier-3'ünüze cevap verir.",
      channels: [
        { k: "E-posta",  v: "support@veridatait.com" },
        { k: "Hotline",  v: "+90 (212) 000-0000" },
        { k: "Portal",   v: "https://veridatait.com/support" },
        { k: "Sev-1",    v: "<15dk yanıt · 7/24" },
      ],
    },
    contact: {
      eyebrow: "Başlayın",
      title: "InfraAI'yi Kendi Altyapınızda Keşfedin",
      sub: "Uzman ekibimizle gerçekleştireceğiniz canlı demoda InfraAI'nin altyapınızı nasıl analiz ettiğini, kök nedeni nasıl belirlediğini ve uygulanabilir aksiyonlar nasıl ürettiğini birlikte inceleyelim.",
      trustBand: [
        {
          k: "Canlı Ürün Demosu",
          v: "InfraAI'nin gerçek ekranlarını, AI motorunu ve uçtan uca analiz süreçlerini görün.",
        },
        {
          k: "Altyapınıza Özel Değerlendirme",
          v: "Mevcut sunucu, veritabanı, middleware ve network yapınıza uygun kullanım senaryolarını birlikte değerlendirelim.",
        },
        {
          k: "Kurulum ve Entegrasyon Planı",
          v: "Mevcut monitoring araçlarınız, operasyon süreçleriniz ve güvenlik gereksinimleriniz doğrultusunda uygulanabilir bir devreye alma planı oluşturalım.",
        },
      ],
      email: "info@infraai.ai",
      emailLabel: "Kurumsal talepler",
      ctaEmail: "Demo için e-posta gönderin",
    },
    hero: {
      eyebrow: "Sürekli Altyapı Güvencesi",
      slideParts: [
        [
          { text: "Altyapınız " },
          { text: "güvende mi?", cyan: true },
        ],
        [
          { text: "İzlemek, bugünün dünyasında artık " },
          { text: "yetmez.", cyan: true },
        ],
        [
          { text: "Geleneksel izleme araçları " },
          { text: "kesintileri engellemez.", cyan: true },
        ],
        [
          { text: "Yapay zeka ile " },
          { text: "öğrenen", cyan: true },
          { text: ", " },
          { text: "tahminleyen", cyan: true },
          { text: " ve " },
          { text: "çözen", cyan: true },
          { text: " akıllı altyapı yönetim sistemi" },
        ],
      ],
      slides: [
        "Altyapınız güvende mi?",
        "İzlemek, bugünün dünyasında artık yetmez.",
        "Geleneksel izleme araçları kesintileri engellemez.",
        "Yapay zeka ile öğrenen, tahminleyen ve çözen akıllı altyapı yönetim sistemi",
      ],
      brandName: "InfraAI",
      brandTagline: "Yeni Nesil Agentic AIOps Destekli İzleme Merkezi",
      ctaPrimary: "Canlı Demo İste",
      ctaSecondary: "AI Motorunu Gör",
      badges: ["24 AI Modeli", "%100 On-Prem AI", "AI Destekli RCA", "4 Katmanlı Kapsam"],
      live: "CANLI AI MOTORU",
      decision: "karar · doğrulandı",
      decisionAgo: "2.4s önce",
    },
    whyInfraAI: {
      eyebrow: "Neden InfraAI",
      title: "Neden ",
      titleSuffix: "?",
      thesisParts: [
        { text: "Çoğu izleme aracı size ", bold: false },
        { text: "ne olduğunu", bold: true },
        { text: " söyler. InfraAI ise ", bold: false },
        { text: "neden", cyan: true },
        { text: " olduğunu, ", bold: false },
        { text: "ne olacağını", cyan: true },
        { text: " ve ", bold: false },
        { text: "ne yapmanız gerektiğini", cyan: true },
        { text: " söyler. Monitoring sizi uyarır — InfraAI sizi hazırlar.", bold: false },
      ],
      chain: [
        { n: "01", verb: "Gör", desc: "Metrik toplar, eşik aşılınca alarm üretir.", rival: true, rivalLabel: "Günümüz izleme sistemleri burada durur" },
        { n: "02", verb: "Anla", desc: "Katmanlar arası olayları ilişkilendirir, kök nedeni bulur.", rival: false },
        { n: "03", verb: "Öngör", desc: "Sorun oluşmadan günler önce sonucu tahmin eder.", rival: false },
        { n: "04", verb: "Çöz", desc: "Uygulanabilir aksiyonu ve komutu hazır sunar.", rival: false },
      ],
      pillars: [
        {
          k: "Düşünür",
          v: "Tek tek metriklere değil, olaylar arasındaki ilişkiye bakar. Alarmın arkasındaki gerçek nedeni ortaya çıkarır.",
          icon: "brain",
          evidence: [
            [
              { t: "process:", tone: "dim" },
              { t: "java", tone: "violet", tag: true },
              { t: " · RAM ", tone: "base" },
              { t: "42→312MB", tone: "dim" },
              { t: " (7g)", tone: "dim" },
            ],
            [
              { t: "→ ", tone: "arrow" },
              { t: "%643 · ", tone: "base" },
              { t: "memory leak", tone: "amber" },
            ],
          ],
        },
        {
          k: "Tahmin Eder",
          v: "Disk dolmadan, RAM tükenmeden, servis etkilenmeden günler önce haber verir. Reaktif değil, proaktif.",
          icon: "trend",
          evidence: [
            [
              { t: "/u01 disk ", tone: "dim" },
              { t: "%94", tone: "cyan", tag: true },
              { t: " · ", tone: "base" },
              { t: "+0.8/gün", tone: "dim" },
            ],
            [
              { t: "→ ", tone: "arrow" },
              { t: "~22 gün", tone: "amber" },
              { t: " sonra dolar", tone: "base" },
            ],
          ],
        },
        {
          k: "Karar Verir",
          v: "Yüzlerce metriği tek tek incelemenize gerek kalmaz. 30 günlük baseline'a göre gerçek anomaliyi ayırır.",
          icon: "target",
          evidence: [
            [
              { t: "SRV-A", tone: "dim" },
              { t: " sapma %0.6 ", tone: "base" },
              { t: "→ ", tone: "arrow" },
              { t: "alarm yok", tone: "green" },
            ],
            [
              { t: "SRV-B", tone: "dim" },
              { t: " sapma %38 ", tone: "base" },
              { t: "→ ", tone: "arrow" },
              { t: "uyarı", tone: "red" },
            ],
          ],
        },
        {
          k: "Aksiyon Alır",
          v: "Sadece alarm üretmez. Çalıştırılacak komutu ve çözüm adımını da verir.",
          icon: "zap",
          evidence: [
            [
              { t: "PROD-04", tone: "green", tag: true },
              { t: " CPU %98 · ", tone: "base" },
              { t: "sebep: oracle", tone: "dim" },
            ],
            [
              { t: "→ ", tone: "arrow" },
              { t: "top -bn1 -o %CPU | head -15", tone: "cyan" },
            ],
          ],
        },
      ],
      stats: [
        { v: "4", k: "katman" },
        { v: "24", k: "AI modeli" },
        { v: "1", k: "karar motoru" },
      ],
      statsTail: "— veriyi aksiyona dönüştürür.",
    },
    gap: {
      eyebrow: "İzleme Boşluğu",
      title: "İzleme Sistemleri yalnızca veriyi gösterir. InfraAI ise nedenini, başlangıcını ve çözümünü sunar.",
      cards: [
        {
          k: "İzlemek ≠ Anlamak",
          v: "Geleneksel izleme araçları metrikleri gösterir. Katmanlar arasındaki ilişkiyi kuramaz, olayların gerçek nedenini açıklayamaz.",
        },
        {
          k: "Uyarı ≠ Çözüm",
          v: "Alarm üretmek yeterli değildir. InfraAI kök nedeni belirler, olası etkileri tahmin eder ve uygulanabilir aksiyonları önerir.",
        },
        {
          k: "Veri ≠ Karar",
          v: "Veri tek başına değer üretmez. InfraAI veriyi analiz eder, önceliklendirir ve karar desteğine dönüştürür.",
        },
      ],
    },
    platform: {
      eyebrow: "Dört Katmanlı Zeka",
      title: "İzle. Analiz Et. Tahmin Et. Optimize Et.",
      tagline: "Tek platform. Tek veri modeli. Uçtan uca altyapı zekâsı.",
      sub: "Altyapınızın tamamını anlayan, kök neden analizi yapan ve uygulanabilir aksiyonlar öneren yapay zekâ platformu.",
      layers: [
        {
          n: "01",
          k: "InfraAI Keşfet",
          v: "Veritabanı, sunucu, ağ ve middleware katmanlarında sürekli telemetri. Tek ajan kümesi, tek birleşik sinyal modeli — kurulum anından itibaren 7/24 proaktif anomali tespiti.",
          bullets: ["DB · Sunucu · Ağ · Middleware", "Process / GC / heap / thread pool", "Latency · bağlantı · DNS · SSL"],
        },
        {
          n: "02",
          k: "InfraAI Akıl Yürüt",
          v: "32 kategorili RCA (Kök Neden Analizi) sınıflandırma motoru, güvenli sınırda çalışan diagnostik sorgular ve yerel LLM ile otomatik rapor üretimi. Her bulgu tekrarlanabilir kanıt ve somut düzeltmeyle gelir.",
          bullets: ["32 RCA kategorisi", "7 katmanlı güvenlik bariyeri (Guardrail)", "Yerel LLM altyapısı ile tam veri gizliliği"],
        },
        {
          n: "03",
          k: "InfraAI Tahmin",
          v: "Prophet ve LSTM modelleriyle CPU, RAM ve disk kaynak gereksinimleri 5 dakikadan 90 güne kadar uzanan bir projeksiyonda öngörülür. Mevsimsel baseline'lar normal deseni gerçek anomaliden ayırır.",
          bullets: ["5 dakikadan 90 güne tahmin ufku", "Tekli ve çoklu değişken analizi (Mevsimsellik destekli)", "24 orkestre AI motoru"],
        },
        {
          n: "04",
          k: "InfraAI Optimize",
          v: "SQL analizi, index ve yeniden yazım önerileri, maliyet optimizasyonu (rightsizing) ve otomatik düzeltme senaryoları (playbook) — BT yönetiminin onayına sunulan, ölçülebilir kazanç tahminleriyle.",
          bullets: ["SQL optimizasyonu", "Maliyet rightsizing", "Tek tıkla güvenli otomasyon"],
        },
      ],
    },
    rca: {
      eyebrow: "Infra AI RCA Akış Diagramı",
      titleLead: "",
      title: "Metrikleri Nasıl Anlamlandırır?",
      sub: "Sunucu, veritabanı, middleware ve network katmanlarından gelen metrikleri ilişkilendirerek kök nedeni belirler ve uygulanabilir aksiyon önerileri üretir.",
      steps: [
        {
          n: "01",
          k: "Sınıflandırıcı Motor",
          v: "32 RCA kategorisi. Sinyal gelir, sınıflandırıcı aileyi (kilit çekişmesi, bellek sızıntısı, IO doygunluğu vb.) güven skoru ile seçer.",
          code: `{
  "incident": "INC-8429",
  "category": "DB_LOCK_CONTENTION",
  "confidence": 0.962,
  "engines_triggered": ["ML.classifier-v3", "rule.db.adv-lock"]
}`,
        },
        {
          n: "02",
          k: "Diagnostik Sorgu",
          v: "Kapsam-sınırlı, salt-okunur diagnostik sorgular SSH / WinRM / SQL üzerinden çalışır. Her komut filtrelenir, timeout'a tabidir ve replay-loglanır.",
          code: `[SSH] linux-prod-07 :: vmstat 1 3 · iostat -xz 1 3
[SQL] db://prod :: session_blockers (salt okunur)
[WinRM] win-app-02 :: Get-Counter '\\Process(*)\\Working Set'
guardrails: command_filter · timeout · sensitive_redaction`,
        },
        {
          n: "03",
          k: "AI Rapor Üreticisi",
          v: "Yerel LLM yapılandırılmış kanıtı alır; insan-okur kök neden + düzeltme planı üretir, halüsinasyon kontrolünden geçirilir.",
          code: `Kök neden:  Uzun süren UPDATE; TX kilidini tutarak
            14 oturumu 38sn bloke etti.
Tetikleyici: Filtre sütununda eksik index
Düzeltme:    CREATE INDEX idx_orders_customer ON ORDERS(customer_id);
Güven:       %91 · hallucination_check: PASS`,
        },
      ],
    },
    metrics: {
      eyebrow: "LAB & PILOT DEPLOYMENT SONUÇLARI",
      title: "Pilot Kurulumlarda İlk 90 Gün Performansı",
      stats: [
        { v: "14×", k: "Altyapı olaylarında ROI", sub: "yalnızca izleme modeli baseline'a göre" },
        { v: "24", k: "AI Modeli", sub: "11 Dashboard AI + 13 SQL AI · ML · istatistik · kural" },
        { v: "%91", k: "%90+ Doğruluk Oranı", sub: "RCA bugün yerel LLM (son 24s)", live: true },
        { v: "%100", k: "On-Prem AI", sub: "Veri dışarı çıkmaz · Ollama yerel · KVKK uyumlu" },
      ],
    },
    capabilities: {
      eyebrow: "AI Operasyon Platformu",
      titleWordmark: true,
      title: "İzlemenin Ötesinde Altyapı Zekâsı",
      sub: "Sunucu, veritabanı, middleware ve network katmanlarını uçtan uca analiz ederek anomalileri, kök nedenleri ve olası sistem etkilerini yapay zekâ ile tespit eden kurumsal yönetim platformu.",
      hook: {
        before: "İzleme araçları problemi gösterir.",
        afterLead: "nedenini bulur, etkisini analiz eder ve ne yapılması gerektiğini önerir.",
      },
      cardsTitle: "AI Yetkinlikleri",
      previewTagline: "Stratejik Teknoloji Ortağınız",
      verticals: [
        {
          k: "Olay İlişkilendirme ve Korelasyon",
          icon: "brain",
          bullets: [
            "Katmanlar arası olay ilişki analizi",
            "Olay zincirinin otomatik oluşturulması",
            "Dağınık alarmların korelasyonu",
            "Tek panelde uçtan uca görünürlük",
          ],
        },
        {
          k: "Otomatize RCA (Kök Neden Analizi)",
          icon: "search",
          bullets: [
            "RCA raporunun otomatik oluşturulması",
            "Etkilenen sistem ve servislerin belirlenmesi",
            "Olası nedenlerin önceliklendirilmesi",
            "Çözüm önerileri ve aksiyon planı",
          ],
        },
        {
          k: "Öngörücü Zekâ & Kapasite Planlama",
          icon: "trend",
          bullets: [
            "Performans eğilimlerini analiz eder",
            "Kaynak tükenmesini öngörür",
            "Kapasite planlamasını destekler",
            "Risk skorları oluşturur",
          ],
        },
        {
          k: "Otonom Karar Motoru",
          icon: "zap",
          bullets: [
            "AI destekli aksiyon önerileri",
            "Önceliklendirilmiş olay yönetimi",
            "Alarm gürültüsünün azaltılması",
            "Karar destek mekanizması",
          ],
        },
        {
          k: "SQL ve Veritabanı Intelligence",
          icon: "layers",
          bullets: [
            "Kritik ve yavaş SQL sorgularının tespiti",
            "İndeks ve sorgu optimizasyon önerileri",
            "Veritabanı performans analizi",
            "Kapasite ve darboğaz öngörüsü",
          ],
        },
        {
          k: "Hibrit & Yerel AI (On-Premises LLM)",
          icon: "lock",
          bullets: [
            "On-prem veya hibrit mimari",
            "Veri dışarı çıkmadan AI analizi",
            "Yerel LLM desteği",
            "Güvenli ve denetlenebilir AI altyapısı",
          ],
        },
      ],
    },
    enterpriseProof: {
      items: [
        { v: "Production Proven", accent: true },
        { v: "13 AI Analiz Motoru" },
        { v: "%100 On-Premise Mimari" },
      ],
      coverage: "Tam yığın kapsam: Veritabanı · İşletim Sistemleri · Network · Storage · Middleware",
      stack: "Oracle · MSSQL · PostgreSQL · Linux · Windows",
      cta: "Teknik Demo Rezerve Et →",
    },
    trust: {
      eyebrow: "Mimari & Uyumluluk",
      title: "Kritik Altyapılar İçin Geliştirildi. Regülasyonlara Uyumlu.",
      sub: "Geleneksel izleme araçları sadece ne olduğunu söyler; InfraAI ne olacağını tahmin eder ve aksiyon önerir.",
      items: [
        {
          k: "%100 On-Premises Altyapı",
          v: "Verileriniz, prompt'larınız ve telemetri metrikleriniz güvenli ağınızın dışına asla çıkmaz. Bulut bağımlılığı yoktur; veri taşınması zorunlu olan regüle ortamlar (Finans, Kamu, Enerji) için tam veri egemenliği sağlar.",
        },
        {
          k: "İlk Günden Optimizasyon",
          v: "Uzun eğitim ve veri toplama süreçlerini beklemeyin. Ön tanımlı yapay zeka modelleri, kurulum anında risk tespiti ve analize başlar.",
        },
        {
          k: "Kurumsal Güvenlik Kontrolleri",
          v: "Komut filtreleme, prompt injection koruması, hassas veri maskeleme ve audit trail (denetim izi) altyapısı ile iç ve dış denetim standartlarını tam karşılar.",
        },
        {
          k: "Proaktif SQL & Performans Analizi",
          v: "Kritik ve yavaşlayan SQL sorgularını, veritabanı performansını ve kapasite darboğazlarını kesinti yaşanmadan önce tespit eder ve raporlar.",
        },
      ],
    },
    stack: {
      eyebrow: "Teknoloji Yığını",
      title: "Düzenlemeli sektörlerin zaten güvendiği bir yığın üzerinde kuruldu",
      groups: [
        { k: "Frontend", v: "React · TypeScript · Tailwind · Recharts" },
        { k: "Backend", v: "FastAPI · SQLAlchemy · Python 3.11" },
        { k: "Veri", v: "TimescaleDB · PostgreSQL · Redis" },
        { k: "AI/ML", v: "Prophet · LSTM · IsolationForest · RandomForest · DecisionTree" },
        { k: "LLM", v: "Ollama · Llama 3.1 · Phi-4 (yerel)" },
        { k: "Bağlantı", v: "SSH · WinRM · SQL native · Native DB AI" },
      ],
    },
    footer: {
      tagline: "Akıllı Altyapı Çözümleri",
      company: "Powered by Veridata IT",
      rights: "Tüm hakları saklıdır.",
      platformLabel: "Platform",
      companyLabel: "Kurumsal",
      legalLabel: "Yasal",
      platformLinks: [
        { k: "AI Yetkinlikleri", href: "#capabilities" },
        { k: "AI Engine Hub", href: "#platform" },
        { k: "RCA Akışı", href: "#rca" },
        { k: "Neden InfraAI?", href: "#why-infraai" },
        { k: "Güvenlik ve Uyumluluk", href: "#trust" },
      ],
    },
  },
};

const I18nContext = createContext({ lang: "tr", t: dict.tr, setLang: () => {} });

export const I18nProvider = ({ children }) => {
  const [lang, setLang] = useState(() => localStorage.getItem("infraai_lang") || "tr");
  useEffect(() => {
    localStorage.setItem("infraai_lang", lang);
    document.documentElement.lang = lang;
  }, [lang]);
  return (
    <I18nContext.Provider value={{ lang, t: dict[lang], setLang }}>
      {children}
    </I18nContext.Provider>
  );
};

export const useI18n = () => useContext(I18nContext);
